import { useMemo } from 'react';

export interface FunnelStage {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface PipelineFunnelChartProps {
  stages: FunnelStage[];
  height?: number;
}

/**
 * Pure SVG funnel chart. Each stage is a rounded trapezoid whose width
 * is proportional to its value. Centered layout, animated gradients.
 */
export const PipelineFunnelChart = ({ stages, height = 240 }: PipelineFunnelChartProps) => {
  const nonZeroStages = useMemo(
    () => stages.filter((s) => s.value > 0),
    [stages],
  );

  if (nonZeroStages.length === 0) return null;

  const n = nonZeroStages.length;
  const svgWidth = 280;
  const maxValue = Math.max(...nonZeroStages.map((s) => s.value));

  const padY = 4;
  const gap = 5;
  const h = Math.max(28, (height - padY * 2 - gap * (n - 1)) / n);
  const maxW = svgWidth * 0.88;
  const minW = svgWidth * 0.26;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${svgWidth} ${height}`}
      className="overflow-visible"
      style={{ filter: 'drop-shadow(0 6px 20px rgba(0,0,0,0.35))' }}
    >
      <defs>
        {nonZeroStages.map((stage, i) => (
          <linearGradient key={`g-${i}`} id={`fg-${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stage.color} stopOpacity={0.92}>
              <animate attributeName="stop-opacity" values="0.92;0.74;0.92" dur="3.5s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stopColor={stage.color} stopOpacity={0.48}>
              <animate attributeName="stop-opacity" values="0.48;0.34;0.48" dur="3.5s" repeatCount="indefinite" />
            </stop>
          </linearGradient>
        ))}
        {nonZeroStages.map((stage, i) => (
          <linearGradient key={`s-${i}`} id={`fs-${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity={0.14} />
            <stop offset="45%" stopColor="white" stopOpacity={0.02} />
            <stop offset="100%" stopColor="white" stopOpacity={0} />
          </linearGradient>
        ))}
        <filter id="fgs" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="2.5" floodColor="#000" floodOpacity="0.4" />
        </filter>
      </defs>

      {nonZeroStages.map((stage, i) => {
        const y = padY + i * (h + gap);

        // Width = value ratio * maxW, clamped to minW
        const ratio = stage.value / maxValue;
        const w = minW + ratio * (maxW - minW);
        const x = (svgWidth - w) / 2;
        const r = Math.min(7, h / 3, w / 5);

        // Symmetric rounded trapezoid
        const pathD = [
          `M ${x + r} ${y}`,
          `L ${x + w - r} ${y}`,
          `Q ${x + w} ${y} ${x + w} ${y + r}`,
          `L ${x + w} ${y + h - r}`,
          `Q ${x + w} ${y + h} ${x + w - r} ${y + h}`,
          `L ${x + r} ${y + h}`,
          `Q ${x} ${y + h} ${x} ${y + h - r}`,
          `L ${x} ${y + r}`,
          `Q ${x} ${y} ${x + r} ${y}`,
          'Z',
        ].join(' ');

        // Shine on top ~38%
        const shH = Math.min(h * 0.38, 16);
        const shW = w * 0.9;
        const shX = (svgWidth - shW) / 2;
        const shineD = [
          `M ${shX + r} ${y + 1}`,
          `L ${shX + shW - r} ${y + 1}`,
          `Q ${shX + shW} ${y + 1} ${shX + shW} ${y + 1 + r}`,
          `L ${shX + shW} ${y + shH}`,
          `L ${shX} ${y + shH}`,
          `L ${shX} ${y + 1 + r}`,
          `Q ${shX} ${y + 1} ${shX + r} ${y + 1}`,
          'Z',
        ].join(' ');

        const displayValue = stage.key === 'discarded'
          ? `-${stage.value.toLocaleString()}`
          : stage.value.toLocaleString();

        const midY = y + h / 2;
        const fontSize = displayValue.length >= 4 ? 11 : 12.5;
        const labelSize = h >= 36 ? 8 : 7;

        return (
          <g key={stage.key} filter="url(#fgs)" className="transition-all duration-200 hover:brightness-115">
            <path
              d={pathD}
              fill={`url(#fg-${i})`}
              stroke={stage.color}
              strokeWidth={0.5}
              strokeOpacity={0.2}
            />
            <path d={shineD} fill={`url(#fs-${i})`} />
            <text
              x={svgWidth / 2}
              y={midY - 3}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="white"
              fontSize={fontSize}
              fontWeight={900}
              fontFamily="inherit"
              style={{ textShadow: '0 1px 6px rgba(0,0,0,0.55)' }}
            >
              {displayValue}
            </text>
            <text
              x={svgWidth / 2}
              y={midY + 11}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="white"
              fontSize={labelSize}
              fontWeight={700}
              fontFamily="inherit"
              opacity={0.58}
              letterSpacing="0.07em"
            >
              {stage.label.toUpperCase()}
            </text>
          </g>
        );
      })}
    </svg>
  );
};